import { useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type DragEvent, type MouseEvent, type ReactNode } from 'react';
import { tone } from '../../../shared/components/tokens.js';
import { useMediaQuery } from '../../../shared/layout.js';
import { Spinner } from '../../../ui/Button.js';
import { cx } from '../../../ui/cx.js';
import { CameraIcon, CheckIcon } from '../../../ui/icons.js';
import { useInputMode } from '../../../ui/useInputMode.js';
import { ImageIcon, PaperclipIcon, SendIcon } from '../../ui/icons-v7.js';
import { Preview, TOUCH_QUERY } from '../photo-picker/PhotoPicker.js';
import type { UploadState } from '../photo-picker/useUploads.js';
import type { Attachments } from './useAttachments.js';

/** Hauteur maximale du champ (environ six lignes) : au-delà, il défile. */
const MAX_FIELD_PX = 168;

/** Les fichiers acceptés sont-ils tous des images ? (Libellés « photo » ; sinon « fichier ».) */
function imagesOnly(accept: string): boolean {
  return accept.split(',').every((a) => a.trim().toLowerCase().startsWith('image/'));
}

/** Le glisser contient-il des fichiers ? (Un texte glissé n'allume pas la zone.) */
function draggingFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  return !types || Array.from(types).includes('Files');
}

/**
 * Zone de conversation où l'on peut **déposer** des photos (ordinateur) : surbrillance au survol,
 * fichiers ajoutés aux pièces jointes de la zone de saisie (mêmes contrôles qu'au sélecteur).
 */
export function AttachDropArea({
  attachments,
  enabled = true,
  children,
  className,
}: {
  attachments: Attachments;
  enabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  if (!enabled) return <div className={className}>{children}</div>;
  const word = imagesOnly(attachments.accept) ? 'photos' : 'fichiers';
  return (
    <div
      data-testid="message-drop-area"
      data-dragging={over || undefined}
      className={cx('relative', className)}
      onDragEnter={(e) => {
        if (!draggingFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!draggingFiles(e)) return;
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        attachments.add(Array.from(e.dataTransfer?.files ?? []));
      }}
    >
      {children}
      {over ? (
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-2xl border-2 border-dashed ${tone.dropActive}`}
        >
          <p className={`rounded-xl px-4 py-2 text-base font-medium ${tone.surface} ${tone.text} shadow-md`}>Déposez vos {word} pour les joindre</p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Zone de saisie d'un message, commune à l'espace garage et au portail (02/10/2026), comme dans les
 * applications de messagerie : un trombone dans la barre pour joindre des photos, le champ qui grandit
 * avec le texte, le bouton Envoyer intégré.
 * - Téléphone : le trombone ouvre un petit menu « Galerie » / « Prendre une photo ».
 * - Ordinateur : le trombone ouvre le sélecteur de fichiers ; on peut aussi déposer des photos sur la
 *   conversation (`AttachDropArea`) ou en coller dans le champ.
 * - Pièces jointes en miniatures au-dessus du champ : retrait, état d'envoi, erreurs claires.
 * - Clavier ouvert (téléphone) : la barre est posée juste au-dessus du clavier, une place de même
 *   hauteur est gardée dans la page (rien ne saute).
 * Envoyer est désactivé sans texte ni pièce jointe. Les types acceptés viennent des pièces jointes
 * (`accept`, images seules par défaut).
 */
export function MessageComposer({
  label,
  placeholder,
  value,
  onValueChange,
  maxLength,
  onSubmit,
  busy = false,
  sendLabel,
  attachments,
  states,
  attachLabel = 'Pièces jointes',
  hint,
  attachNote,
  feedback,
}: {
  /** Nom du champ (lecteurs d'écran ; non affiché). */
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  maxLength: number;
  onSubmit: () => void;
  busy?: boolean;
  /** Nom accessible du bouton d'envoi (« Envoyer », « Envoyer ma question »). */
  sendLabel: string;
  /** Pièces jointes ; absentes : pas de trombone (droit manquant). */
  attachments?: Attachments | null;
  /** État d'envoi de chaque fichier (`useUploads`). */
  states?: Map<File, UploadState>;
  /** Nom de la liste des miniatures. */
  attachLabel?: string;
  /** Ligne d'aide discrète sous la barre. */
  hint?: ReactNode;
  /** Mention sous les miniatures (ex. « Visibles du client à l'envoi. »). */
  attachNote?: ReactNode;
  /** Message d'erreur ou de confirmation, affiché au-dessus de la barre. */
  feedback?: ReactNode;
}) {
  const touch = useMediaQuery(TOUCH_QUERY);
  const ids = { field: useId(), hint: useId(), menu: useId() };
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const pickRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const menuRootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [root, setRoot] = useState<HTMLFormElement | null>(null);
  const [flowHeight, setFlowHeight] = useState(0);
  const { typing, viewportTop, viewportHeight, active } = useInputMode();
  const floating = typing && !!root && !!active && root.contains(active);

  const files = attachments?.files ?? [];
  const accept = attachments?.accept ?? 'image/*';
  const photos = imagesOnly(accept);
  const canSend = !busy && (value.trim().length > 0 || files.length > 0);

  // Champ qui grandit avec le texte, jusqu'à quelques lignes.
  useLayoutEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = el.scrollHeight;
    if (h > 0) el.style.height = `${Math.min(h, MAX_FIELD_PX)}px`;
  }, [value]);

  // Hauteur de la barre dans la page, gardée en place quand elle flotte au-dessus du clavier.
  useEffect(() => {
    if (!root || floating || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setFlowHeight(root.offsetHeight));
    observer.observe(root);
    return () => observer.disconnect();
  }, [root, floating]);

  // Menu « Galerie / Prendre une photo » : fermé par un appui ailleurs ou par Échap.
  useEffect(() => {
    if (!menuOpen) return;
    menuRootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (e: PointerEvent) => {
      if (menuRootRef.current && !menuRootRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [menuOpen]);

  const submit = () => {
    if (canSend) onSubmit();
  };
  // Le champ garde le focus pendant l'appui (sinon le clavier se ferme et la barre change de place).
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  const attachName = photos ? 'Joindre une photo' : 'Joindre un fichier';
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    attachments?.add(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const done = states ? files.filter((f) => states.get(f)?.state === 'done').length : 0;
  const sending = states ? files.some((f) => ['uploading', 'waiting'].includes(states.get(f)?.state ?? '')) : false;
  const uploadErrors = states
    ? files.flatMap((f, i) => {
        const st = states.get(f);
        return st?.state === 'error' ? [{ i, message: st.message }] : [];
      })
    : [];
  const room = attachments?.limits.max !== undefined ? Math.max(0, attachments.limits.max - files.length) : undefined;
  const unit = photos ? 'photo' : 'fichier';

  return (
    <>
      {floating ? <div aria-hidden style={{ height: flowHeight }} /> : null}
      <form
        ref={setRoot}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        data-composer={floating ? 'typing' : 'flow'}
        className={cx(
          floating
            ? 'fixed inset-x-0 z-40 -translate-y-full border-t border-line bg-surface/97 px-[var(--page-x)] py-2 shadow-md backdrop-blur-md'
            : 'mt-3',
        )}
        style={floating ? { top: viewportTop + viewportHeight } : undefined}
      >
        <div className={cx('space-y-2', floating && 'mx-auto max-w-3xl')}>
          {feedback}

          {attachments && attachments.rejected.length > 0 ? (
            <ul role="alert" className={`space-y-1 rounded-xl border p-3 text-sm ${tone.dangerBox}`}>
              {attachments.rejected.map((r, i) => (
                <li key={`${r.name}-${i}`}>{r.message}</li>
              ))}
            </ul>
          ) : null}

          {files.length > 0 ? (
            <div>
              <ul aria-label={attachLabel} className="flex gap-2 overflow-x-auto pb-1">
                {files.map((f, i) => {
                  const st = states?.get(f);
                  const locked = busy || st?.state === 'uploading' || st?.state === 'done';
                  return (
                    <li
                      key={`${f.name}-${f.size}-${i}`}
                      className={cx(
                        'relative size-18 shrink-0 overflow-hidden rounded-xl ring-1',
                        st?.state === 'error' ? 'ring-2 ring-danger' : 'ring-line',
                      )}
                    >
                      <Preview file={f} alt={`${photos ? 'Photo' : 'Fichier'} ${i + 1}`} className="size-full object-cover" />
                      {st?.state === 'uploading' || st?.state === 'waiting' ? (
                        <span data-testid="upload-state" className={`absolute inset-0 flex items-center justify-center ${tone.overlay}`}>
                          <Spinner />
                          <span className="sr-only">{st.state === 'uploading' ? 'Envoi…' : 'En attente'}</span>
                        </span>
                      ) : null}
                      {st?.state === 'done' ? (
                        <span data-testid="upload-state" className="absolute bottom-1 left-1 flex size-6 items-center justify-center rounded-full bg-success text-surface">
                          <CheckIcon size={16} strokeWidth={2} />
                          <span className="sr-only">Envoyée</span>
                        </span>
                      ) : null}
                      {!locked ? (
                        <button
                          type="button"
                          aria-label={`Retirer ${photos ? 'la photo' : 'le fichier'} ${i + 1}`}
                          onMouseDown={keepFocus}
                          onClick={() => attachments?.remove(i)}
                          className="absolute right-0 top-0 flex size-11 items-start justify-end p-1"
                        >
                          <span aria-hidden className={`flex size-6 items-center justify-center rounded-full text-base leading-none ${tone.overlay}`}>
                            ×
                          </span>
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {sending || done > 0 ? (
                <div className="mt-1 flex items-center gap-2">
                  <div
                    role="progressbar"
                    aria-label={`Envoi des ${unit}s`}
                    aria-valuemin={0}
                    aria-valuemax={files.length}
                    aria-valuenow={done}
                    className={`h-1.5 flex-1 overflow-hidden rounded-full ${tone.surfaceMuted}`}
                  >
                    <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(done / files.length) * 100}%` }} />
                  </div>
                  <span className={`text-xs ${tone.textMuted}`}>
                    {done}/{files.length} envoyée{done > 1 ? 's' : ''}
                  </span>
                </div>
              ) : room !== undefined ? (
                <p className={`text-xs ${tone.textMuted}`}>
                  {files.length} {unit}
                  {files.length > 1 ? 's' : ''} · {room > 0 ? `encore ${room} possible${room > 1 ? 's' : ''}` : 'maximum atteint'}
                  {attachNote ? <> · {attachNote}</> : null}
                </p>
              ) : attachNote ? (
                <p className={`text-xs ${tone.textMuted}`}>{attachNote}</p>
              ) : null}
              {uploadErrors.length > 0 ? (
                <ul role="alert" className={`mt-1 space-y-0.5 text-sm ${tone.danger}`}>
                  {uploadErrors.map((e) => (
                    <li key={e.i}>
                      {photos ? 'Photo' : 'Fichier'} {e.i + 1} : {e.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-end gap-1 rounded-2xl border border-line-strong bg-surface p-1 shadow-xs focus-within:border-brand-strong focus-within:ring-3 focus-within:ring-brand-strong/20">
            {attachments ? (
              <div ref={menuRootRef} className="relative shrink-0">
                <button
                  type="button"
                  aria-label={attachName}
                  title={attachName}
                  disabled={busy}
                  onMouseDown={touch ? undefined : keepFocus}
                  {...(touch ? { 'aria-haspopup': 'menu' as const, 'aria-expanded': menuOpen, 'aria-controls': menuOpen ? ids.menu : undefined } : {})}
                  onClick={() => (touch ? setMenuOpen((v) => !v) : pickRef.current?.click())}
                  className="flex size-(--ctl-md) items-center justify-center rounded-xl text-ink-2 hover:bg-sunken hover:text-ink disabled:opacity-45 focus-visible:outline-2 focus-visible:outline-focus"
                >
                  <PaperclipIcon size={22} />
                </button>
                {touch && menuOpen ? (
                  <div
                    id={ids.menu}
                    role="menu"
                    aria-label={attachName}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setMenuOpen(false);
                        e.preventDefault();
                      }
                    }}
                    className="absolute bottom-full left-0 z-40 mb-2 min-w-56 overflow-hidden rounded-2xl bg-surface-raised p-1.5 text-ink shadow-lg ring-1 ring-line"
                  >
                    {[
                      { key: 'gallery' as const, label: 'Galerie', icon: <ImageIcon size={20} /> },
                      { key: 'camera' as const, label: 'Prendre une photo', icon: <CameraIcon size={20} /> },
                    ]
                      .filter((item) => photos || item.key !== 'camera')
                      .map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => {
                            setMenuOpen(false);
                            (item.key === 'camera' ? cameraRef : pickRef).current?.click();
                          }}
                          className="flex min-h-(--ctl-row) w-full items-center gap-3 rounded-lg px-3 text-left text-base font-medium text-ink outline-none hover:bg-sunken focus-visible:bg-sunken"
                        >
                          <span aria-hidden className="text-ink-3">
                            {item.icon}
                          </span>
                          {item.label}
                        </button>
                      ))}
                  </div>
                ) : null}
                <input
                  ref={pickRef}
                  type="file"
                  accept={accept}
                  multiple
                  className="sr-only"
                  tabIndex={-1}
                  aria-label={photos ? 'Choisir des photos' : 'Choisir des fichiers'}
                  disabled={busy}
                  onChange={pick}
                />
                {photos ? (
                  <input
                    ref={cameraRef}
                    type="file"
                    accept={accept}
                    capture="environment"
                    className="sr-only"
                    tabIndex={-1}
                    aria-label="Prendre une photo avec l’appareil"
                    disabled={busy}
                    onChange={pick}
                  />
                ) : null}
              </div>
            ) : null}
            <label htmlFor={ids.field} className="sr-only">
              {label}
            </label>
            <textarea
              ref={fieldRef}
              id={ids.field}
              rows={1}
              value={value}
              maxLength={maxLength}
              placeholder={placeholder}
              aria-describedby={hint ? ids.hint : undefined}
              onChange={(e) => onValueChange(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl (ou Cmd) + Entrée envoie ; Entrée seule va à la ligne.
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              onPaste={(e) => {
                const pasted = Array.from(e.clipboardData?.files ?? []);
                if (attachments && pasted.length > 0) {
                  e.preventDefault();
                  attachments.add(pasted);
                }
              }}
              className={cx(
                'block min-h-(--ctl-md) min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-2 py-2.5 text-base leading-snug text-ink pointer-fine:py-1.5',
                'placeholder:text-ink-3 focus:outline-none',
                !attachments && 'pl-3',
              )}
              style={{ maxHeight: MAX_FIELD_PX }}
            />
            <button
              type="submit"
              aria-label={sendLabel}
              title={sendLabel}
              disabled={!canSend}
              aria-busy={busy || undefined}
              onMouseDown={keepFocus}
              className="flex size-(--ctl-md) shrink-0 items-center justify-center rounded-xl bg-brand text-on-brand transition-[filter,opacity] hover:brightness-[1.06] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            >
              {busy ? <Spinner /> : <SendIcon size={22} />}
            </button>
          </div>
          {hint ? (
            <p id={ids.hint} className={`px-1 text-xs ${tone.textMuted}`}>
              {hint}
            </p>
          ) : null}
        </div>
      </form>
    </>
  );
}
